import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Logo } from "@/components/Logo";
import { ArrowLeft, ArrowUpRight } from "@phosphor-icons/react";

const COMPARISONS = [
  {
    slug: "/alternative-a-cloudbeds-italia",
    title: "Otelya vs Cloudbeds",
    desc: "Il PMS americano Cloudbeds costa $195/mese e manca di social autopilot. Otelya costa €119/mese e include Facebook + Instagram automatici.",
    tag: "PMS internazionale",
  },
  {
    slug: "/otelya-vs-otello-ai",
    title: "Otelya vs Otello AI",
    desc: "Due nomi simili, due filosofie diverse. Otelya è per B&B 4-50 camere; Otello AI serve medie strutture. Confronto onesto delle 13 differenze chiave.",
    tag: "Italiano · diretto",
  },
  {
    slug: "/otelya-vs-hijiffy",
    title: "Otelya vs HiJiffy",
    desc: "HiJiffy è un chatbot portoghese enterprise da ~$200/mese. Otelya è l'alternativa italiana a €119/mese con Social Autopilot incluso.",
    tag: "Enterprise · EU",
  },
  {
    slug: "/otelya-vs-asksuite",
    title: "Otelya vs Asksuite",
    desc: "Asksuite è brasiliana, focalizzata su OTA→direct booking. Otelya copre tutto il ciclo di vita ospite in italiano, da €119/mese.",
    tag: "Brasile · booking focus",
  },
  {
    slug: "/whatsapp-automatico-hotel",
    title: "WhatsApp automatico vs ChatBot generico",
    desc: "WhatsApp Business con AI multilingua Claude vs ManyChat/SendPulse: perché risposte 'tree-based' non bastano più nel 2026.",
    tag: "Caso d'uso · WhatsApp",
  },
  {
    slug: "/receptionist-24-7-bnb",
    title: "Receptionist AI vs receptionist umana part-time",
    desc: "€119/mese per 24/7 e 30 lingue vs €1.400-2.200/mese per 20-25 ore e 1-3 lingue. Il confronto onesto per un B&B 4-12 camere.",
    tag: "Caso d'uso · ROI",
  },
];

export default function Compare() {
  return (
    <div className="min-h-screen bg-canvas text-ink" data-testid="compare-page">
      <Helmet>
        <html lang="it" />
        <title>Confronti: Otelya vs tutti i concorrenti · Otelya</title>
        <meta name="description" content="Otelya a confronto con Cloudbeds, Otello AI, HiJiffy, Asksuite. Prezzi, lingue, funzionalità, social autopilot. 6 pagine di confronto onesto per scegliere la receptionist AI giusta." />
        <link rel="canonical" href="https://otelya.app/compare" />
        <meta property="og:title" content="Confronti: Otelya vs tutti i concorrenti" />
        <meta property="og:description" content="Prezzi, lingue, funzionalità. 6 pagine di confronto onesto." />
        <meta property="og:url" content="https://otelya.app/compare" />
      </Helmet>

      <nav className="border-b border-line/40">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <Link to="/" data-testid="compare-back-home"><Logo /></Link>
          <Link to="/" className="text-sm text-stone hover:text-terracotta transition-colors flex items-center gap-1">
            <ArrowLeft size={14} /> Torna alla home
          </Link>
        </div>
      </nav>

      <article className="max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="inline-flex items-center gap-2 rounded-full bg-sand px-3 py-1.5 text-xs tracking-wide text-stone uppercase">
          Confronti · aggiornato Febbraio 2026
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink mt-6">
          Otelya a confronto con <em className="italic text-terracotta">chiunque</em>.
        </h1>
        <p className="mt-6 text-lg text-stone leading-relaxed max-w-3xl">
          Non pensiamo di essere l'unica receptionist AI per hotel. Ci sono alternative valide, ognuna con punti forti. Ecco un confronto onesto, dato per dato, per aiutarti a scegliere quella giusta per il tuo caso.
        </p>

        <div className="mt-16 grid md:grid-cols-2 gap-6">
          {COMPARISONS.map((c, i) => (
            <Link
              key={c.slug}
              to={c.slug}
              className="group rounded-2xl border border-line bg-canvas p-6 hover:border-terracotta transition-colors"
              data-testid={`compare-card-${i}`}
            >
              <div className="text-xs text-stone uppercase tracking-wide">{c.tag}</div>
              <h2 className="mt-2 font-serif text-2xl text-ink group-hover:text-terracotta transition-colors">
                {c.title}
              </h2>
              <p className="mt-3 text-sm text-stone leading-relaxed">{c.desc}</p>
              <div className="mt-4 text-xs text-terracotta flex items-center gap-1">
                Leggi il confronto <ArrowUpRight weight="bold" size={12} />
              </div>
            </Link>
          ))}
        </div>

        <section className="mt-20 rounded-3xl bg-sand/40 p-8 sm:p-12">
          <h2 className="font-serif text-3xl text-ink">Non trovi il confronto che cerchi?</h2>
          <p className="mt-3 text-stone max-w-2xl">
            Se stai valutando un altro prodotto e vuoi un parere onesto sul confronto con Otelya, scrivi direttamente ad Alfio.
          </p>
          <div className="mt-6">
            <a href="mailto:hello@otelya.app?subject=Confronto%20Otelya%20vs" className="text-sm text-terracotta hover:underline" data-testid="compare-contact-link">
              hello@otelya.app →
            </a>
          </div>
        </section>
      </article>
    </div>
  );
}
